<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-notice-container">
    <div class="print-notice-options">
      <div class="print-notice-type">
        <Dropdown
          placeholder="Notice Type"
          :selectedObject.sync="selectedNoticeType"
          :errorMessage.sync="noticeTypeErr"
          :items="noticeTypeList"
          itemKey="Notice Type"
          ref="noticeTypeErr"
          @selected="getFilterTypeList()"
          :isRequired="true"
        />
        <Dropdown
          placeholder="Haulier Code"
          :selectedObject.sync="selectedHaulierCode"
          :errorMessage.sync="haulierCodeErr"
          :items="haulierCodeList"
          itemKey="haulierDisplayText"
          ref="haulierCodeErr"
          :isRequired="true"
        />
      </div>
      <div class="containers-selection">
        <div style="display: flex; justify-content: flex-end; gap: 10px">
          <div style="display: flex; align-items: center; justify-content: center">
            <span> {{ containerFiltersSummary }} </span>
          </div>
          <ButtonFilter
            buttonText="Change Filter"
            filterPosition="right"
            :filterWidth="420"
            :isOpen.sync="containerFilterIsOpen"
            :buttonIsDisabled="containerFilterButtonIsDisabled"
          >
            <BoxContainer title="Filter" :isCheckBox="true" :isChecked.sync="filterChecked">
              <div style="display: flex; flex-direction: column">
                <Dropdown
                  placeholder="Filter By"
                  :selectedObject.sync="selectedFilter"
                  :errorMessage.sync="selectedFilterErr"
                  :items="filterTypeList"
                  :isDisabled="!filterChecked"
                  :isRequired="filterChecked"
                  ref="selectedFilterErr"
                  itemKey="filtertype"
                  @selected="updateMatchValueList"
                />
                <Dropdown
                  placeholder="Filter Criteria"
                  :selectedObject.sync="selectedFilterCondition"
                  :errorMessage.sync="selectedFilterConditionErr"
                  :items="filterConditionList"
                  :isDisabled="!filterChecked"
                  :isRequired="filterChecked"
                  itemKey="condition"
                  ref="selectedFilterConditionErr"
                  v-if="showFilterCriteria"
                />
                <InputQuery
                  placeholder="Search"
                  :selectedObject.sync="selectedMatchValue"
                  :errorMessage.sync="selectedMatchValueErr"
                  :items="filterMatchValueList"
                  :isDisabled="!filterChecked"
                  :isRequired="filterChecked"
                  ref="selectedMatchValueErr"
                  itemKey="displayText"
                  v-if="showFilterCriteria"
                />
                <DateTimePicker
                  :showTime="false"
                  placeholder="Start Date"
                  :value.sync="filterStartDate"
                  :errorMessage.sync="filterStartDateErr"
                  :isRequired="filterChecked"
                  :isDisabled="!filterChecked"
                  v-if="showDateTimePicker"
                />
                <DateTimePicker
                  :showTime="false"
                  placeholder="End Date"
                  :value.sync="filterEndDate"
                  :isRequired="filterChecked"
                  :isDisabled="!filterChecked"
                  :errorMessage.sync="filterEndDateErr"
                  v-if="showDateTimePicker"
                />
                <CheckBoxV2
                  title="Hide Collected"
                  :isChecked.sync="containerHideCollectedIsChecked"
                  style="display: flex; justify-content: flex-start"
                />
              </div>
            </BoxContainer>

            <div class="refresh-button-container">
              <button @click="refreshClicked"> Refresh </button>
            </div>
          </ButtonFilter>

          <button :disabled="selectDeselectAllButtonIsDisabled" @click="selectDeselectAllClicked">
            {{ selectDeselectAllButtonText }}
          </button>
        </div>
      </div>

      <TableBox
        title="Containers Selection"
        :tableData.sync="containersListMaster"
        :selectedRow.sync="containersTableBoxSelectedRow"
        :maxSelection="-1"
        style="max-height: 30vh; max-width: 47vw"
      />

      <PopupV2
        class="email-notice-popup"
        icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
        :show="showEmailNoticePopup"
        :buttons="emailNoticePopupButtons"
        title="Email Notice"
        width="60vh"
        @buttonClicked="emailNoticePopupButtonClicked"
      >
        <div class="email-notice-popup-content-container">
          <div class="mail-inputs-container">
            <div class="email-input-container">
              <EmailInput :mails.sync="emailNoticePopupToMails" title="To" />
            </div>
            <div class="email-input-container">
              <EmailInput :mails.sync="emailNoticePopupCCMails" title="CC">
                <span class="note">Your email will be automatically included in the CC</span>
              </EmailInput>
            </div>
            <div class="email-input-container">
              <EmailInput :mails.sync="emailNoticeePopupBCCMails" title="BCC" />
            </div>
          </div>
          <div class="email-content-inputs-container">
            <Input :inputValue.sync="emailNoticePopupSubjectInputValue" placeholder="Subject" validator="text,100" />
            <RemarksBox title="Message" :text.sync="emailNoticePopupMessageText" :maxLength="850" />
          </div>
        </div>
      </PopupV2>

      <div class="notice-information" style="margin-top: 7px">
        <label style="font-weight: bold; font-size: 1.2em">Notice Information</label>
        <BoxContainer title="Header" style="padding: 10px">
          <DateTimePicker
            :showTime="false"
            placeholder="Date"
            :value.sync="noticeDateInputValue"
            :errorMessage.sync="noticeDateErr"
            :maxDate="smallDateTimeMaxDate"
            :minDate="smallDateTimeMinValue"
            @dateChanged="checkDateTime('noticeDateInputValue', $event)"
          />
          <Input
            placeholder="To"
            :inputValue.sync="toInputValue"
            :errorMessage.sync="toInputErr"
            ref="toInputErr"
            validator="text,40"
          />
          <div class="to-information">
            <Input
              placeholder="Attn"
              :inputValue.sync="attnInputValue"
              :errorMessage.sync="attnInputErr"
              ref="attnInputErr"
              validator="text,40"
            />
            <Input
              placeholder="Tel #"
              :inputValue.sync="telInputValue"
              :errorMessage.sync="telInputErr"
              ref="telInputErr"
              validator="text,20"
            />
            <Input
              placeholder="Fax #"
              :inputValue.sync="faxInputValue"
              :errorMessage.sync="faxInputErr"
              ref="faxInputErr"
              validator="text,20"
            />
          </div>

          <!-- v-if Notice Type == Container Ready for Collection-->
          <div v-if="showCCInputFields">
            <div>
              <Input
                placeholder="CC"
                :inputValue.sync="ccInputValue"
                :errorMessage.sync="ccInputErr"
                ref="ccInputErr"
                validator="text,40"
              />
            </div>
            <div class="to-information">
              <Input
                placeholder="Attn"
                :inputValue.sync="ccAttnInputValue"
                :errorMessage.sync="ccAttnInputErr"
                ref="ccAttnInputErr"
                validator="text,40"
              />
              <Input
                placeholder="Tel #"
                :inputValue.sync="ccTelInputValue"
                :errorMessage.sync="ccTelInputErr"
                ref="ccTelInputErr"
                validator="text,20"
              />
              <Input
                placeholder="Fax #"
                :inputValue.sync="ccFaxInputValue"
                :errorMessage.sync="ccFaxInputErr"
                ref="ccFaxInputErr"
                validator="text,20"
              />
            </div>
          </div>
        </BoxContainer>
        <BoxContainer title="Footer" style="padding: 10px">
          <RemarksBox title="Remarks" :text.sync="remarksInputValue" style="height: 90px" :maxLength="850" />
          <Input placeholder="Regards" :inputValue.sync="regardsInputValue" validator="text,40" />
        </BoxContainer>
      </div>

      <div style="display: flex; margin-top: 10px; justify-content: space-between">
        <div class="formatting-checkbox">
          <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showCompanyLetterHeadIsChecked" />
        </div>
        <div>
          <button @click="previewNoticeClicked">Preview Notice</button>
        </div>
      </div>
    </div>
    <div class="print-notice-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canToggleBackground="false"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        @shareClicked="designerV3ShareClicked"
        ref="designerV3"
      />
      <div v-else style="display: flex; justify-content: center; text-align: center; font-size: 20px">
        <span>Select some notices and click on <strong>Preview Notice</strong> to get started</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    selectDeselectAllButtonIsDisabled: function () {
      return this.containersListMaster.length === 0;
    },
    selectDeselectAllButtonText: function () {
      if (this.containersListMaster.length === 0) {
        return 'Select All';
      }

      return this.containersListMaster.length !== this.containersTableBoxSelectedRow.length ?
          'Select All'
        : 'Deselect All';
    },
    showFilterCriteria: function () {
      const ccInputConditions = ['Customer Code', 'Destination Code', 'Job No', 'Vessel Name'];
      return this.selectedFilter && ccInputConditions.includes(this.selectedFilter.filtertype);
    },
    showDateTimePicker: function () {
      const dateTimePickerConditions = ['Vessel ETA', 'RFC Request Date', 'RFC Date', 'Required Date'];
      return this.selectedFilter && dateTimePickerConditions.includes(this.selectedFilter.filtertype);
    },
    showCCInputFields: function () {
      if (!this.selectedNoticeType || !this.selectedNoticeType['Notice Type']) {
        return false;
      }
      return this.selectedNoticeType['Notice Type'].endsWith('Container Ready For Collection');
    },
  },
  data: function () {
    return {
      // User info
      userDetails: null,

      // Print Notice Options
      noticeTypeList: [],
      haulierCodeList: [],
      selectedNoticeType: null,
      selectedHaulierCode: null,
      noticeTypeErr: '',
      haulierCodeErr: '',

      // Notice Information
      toInputValue: '',
      attnInputValue: '',
      telInputValue: '',
      faxInputValue: '',
      ccInputValue: '',
      ccAttnInputValue: '',
      ccTelInputValue: '',
      ccFaxInputValue: '',
      remarksInputValue: '',
      regardsInputValue: '',
      noticeDateInputValue: new Date(),

      toInputErr: '',
      attnInputErr: '',
      telInputErr: '',
      faxInputErr: '',
      ccInputErr: '',
      ccAttnInputErr: '',
      ccTelInputErr: '',
      ccFaxInputErr: '',
      noticeDateErr: '',

      // Email Information
      showEmailNoticePopup: false,
      emailNoticePopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailNoticePopupToMails: [],
      emailNoticePopupCCMails: [],
      emailNoticeePopupBCCMails: [],
      emailNoticePopupSubjectInputValue: '',
      emailNoticePopupMessageText: '',

      // Containers Selection
      containerFilterIsOpen: false,
      containerFilterButtonIsDisabled: false,
      containerFiltersSummary: '',

      filterChecked: false,
      selectedFilter: null,
      selectedFilterCondition: null,
      selectedMatchValue: null,
      containersListMaster: [],
      containersTableBoxSelectedRow: [],
      filterTypeList: [],
      filterConditionList: [
        { condition: 'Equals', conditionno: '0' },
        { condition: 'Starts With', conditionno: '1' },
        { condition: 'Ends With', conditionno: '2' },
        { condition: 'Contains', conditionno: '3' },
      ],
      filterMatchValueList: [],
      filterStartDate: new Date(),
      filterEndDate: new Date(),
      containerHideCollectedIsChecked: false,

      selectedFilterErr: '',
      selectedFilterConditionErr: '',
      selectedMatchValueErr: '',
      filterStartDateErr: '',
      filterEndDateErr: '',

      // Letter format checkbox
      showCompanyLetterHeadIsChecked: false,

      // Print Notices Preview
      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: {},
      designerV3ShareBehaviour: { behaviour: 'uploadAndEmit' },

      smallDateTimeMinValue: new Date(2000, 0, 1),
      smallDateTimeMaxDate: new Date(2079, 0, 1),
    };
  },
  props: {},
  methods: {
    refreshClicked: async function () {
      let error = false;

      if (!this.selectedNoticeType) {
        this.noticeTypeErr = 'Notice Type is Required!';
        error = true;
      }

      if (!this.selectedHaulierCode) {
        this.haulierCodeErr = 'Haulier Code is Required!';
        error = true;
      }

      if (this.filterChecked) {
        if (!this.selectedFilter) {
          this.selectedFilterErr = 'Filter is Required!';
          error = true;
        }
        if (this.showFilterCriteria) {
          if (!this.selectedFilterCondition) {
            this.selectedFilterConditionErr = 'Filter Conditiion is Required!';
            error = true;
          }
          if (!this.selectedMatchValue) {
            this.selectedMatchValueErr = 'Match Value is Required!';
            error = true;
          }
        } else {
          if (!this.filterStartDate) {
            this.filterStartDateErr = 'Start Date is Required!';
            error = true;
          }

          if (!this.filterEndDate) {
            this.filterEndDateErr = 'End Date is Required!';
            error = true;
          }
        }
      }

      if (error) {
        return;
      }

      this.$fullscreenLoading('Getting Containers');

      this.containerFilterIsOpen = false;
      this.containerFilterButtonIsDisabled = true;
      this.updateContainerFiltersSummary();
      await this.getFilteredContainersList();
      this.containerFilterButtonIsDisabled = false;

      this.$fullscreenLoading();
    },
    getFilteredContainersList: async function () {
      const requestBody = {};

      requestBody.hauliercode = this.selectedHaulierCode['Haulier Code'];
      requestBody.noticenumber = this.selectedNoticeType['Notice Number'];

      if (this.selectedFilter && this.filterChecked) {
        requestBody.filterby = this.selectedFilter.filtertype;
      }

      if (this.selectedFilterCondition && this.filterChecked) {
        requestBody.filtercriteria = this.selectedFilterCondition.conditionno;
      }

      if (this.selectedMatchValue && this.filterChecked) {
        requestBody.matchvalue = this.selectedMatchValue[this.selectedFilter.filtertype];
      }

      if (this.filterStartDate && this.filterChecked) {
        requestBody.filterdatebefore = this.filterStartDate.formatString('yyyy-MM-dd');
      }

      if (this.filterEndDate && this.filterChecked) {
        requestBody.filterdateafter = this.filterEndDate.formatString('yyyy-MM-dd');
      }

      requestBody.hidecollected = this.containerHideCollectedIsChecked;

      this.containersListMaster = [];
      this.containersTableBoxSelectedRow = [];
      const containerList = await this.$apiCallV2('/PrintNotice/ContainerSelection', requestBody);

      if (containerList[0]?.Results === '1') {
        this.$showAlert('error', 'Error', containerList[0]?.ErrMsg || 'Unknown error');
      } else {
        this.containersListMaster = containerList;
      }
    },
    updateContainerFiltersSummary: function () {
      const filtersList = [];

      if (this.selectedFilter && this.filterChecked) {
        filtersList.push(this.selectedFilter.filtertype);
      }

      if (this.selectedFilterCondition && this.filterChecked) {
        filtersList.push(this.selectedFilterCondition.condition);
      }

      if (this.selectedMatchValue && this.filterChecked) {
        filtersList.push(this.selectedMatchValue[this.selectedFilter.filtertype]);
      }

      if (this.filterChecked) {
        this.containerFiltersSummary = `Filter by ${filtersList.join(',')}`;
      } else {
        this.containerFiltersSummary = 'Show All Containers';
      }
    },

    selectDeselectAllClicked: function (_pointerEvent) {
      if (this.containersListMaster.length !== this.containersTableBoxSelectedRow.length) {
        this.containersTableBoxSelectedRow = [];
        this.$nextTick(() => {
          this.containersTableBoxSelectedRow = [...this.containersListMaster];
        });
      } else {
        this.containersTableBoxSelectedRow = [];
      }
    },
    previewNoticeClicked: function () {
      // No selected containers
      if (this.containersTableBoxSelectedRow.length == 0) {
        this.$showAlert('error', 'No Containers Selected', 'Please select some containers to preview the notice');
        return;
      }

      // Error checking for Notice Type & Haulier Code
      let error = false;
      if (!this.selectedNoticeType) {
        this.noticeTypeErr = 'Notice Type is Required!';
        error = true;
      }
      if (!this.selectedHaulierCode) {
        this.haulierCodeErr = 'Haulier Code is Required!';
        error = true;
      }
      if (this.checkDateTime('noticeDateInputValue', this.noticeDateInputValue)) {
        return;
      }

      if (error) {
        return;
      }

      // After error checking
      const templateFileNames = [];
      const formattedNoticeDetails = [];

      switch (this.selectedNoticeType['Notice Number']) {
        case '1': // Container Ready For Collection
          templateFileNames.push('PrintNoticeCRFC.json');
          formattedNoticeDetails.push({
            toValue: this.toInputValue,
            firstAttn: this.attnInputValue,
            firstTel: this.telInputValue,
            firstFax: this.faxInputValue,
            cc: this.ccInputValue ? this.ccInputValue : '',
            secondAttn: this.ccAttnInputValue ? this.ccAttnInputValue : '',
            secondTel: this.ccTelInputValue ? this.ccTelInputValue : '',
            secondFax: this.ccFaxInputValue ? this.ccFaxInputValue : '',
            dateValue: this.noticeDateInputValue ? this.noticeDateInputValue.formatString('dd-MM-yyyy') : '',
            remarksValue: this.remarksInputValue ? this.remarksInputValue : '',
            fromCompany: this.regardsInputValue ? this.regardsInputValue : '',
            fromEmail: this.regardsInputValue ? this.userDetails.Email : '',
            containerArray: this.containersTableBoxSelectedRow.map((row, index) => ({
              num: index + 1,
              requestNo: row['Hau Request No'],
              collectDate: row['Advice Collect Date'],
              lastDate: row['Close Date'],
              containerNo: row['Cont #'],
              containerSize: row['Size'],
              containerType: row['Cont Type'],
              shipper: row['Company'],
            })),
            _showCompanyLetterHead: this.showCompanyLetterHeadIsChecked,
          });
          break;
        case '2': // Daily Outstanding Containers For Delivery
          templateFileNames.push('PrintNoticeDOCFD.json');
          formattedNoticeDetails.push({
            dateValue: this.noticeDateInputValue ? this.noticeDateInputValue.formatString('dd-MM-yyyy') : '',
            toInputValue: this.toInputValue,
            attnInputValue: this.attnInputValue,
            telInputValue: this.telInputValue,
            faxInputValue: this.faxInputValue,
            dateInputValue:
              this.noticeDateInputValue ? this.noticeDateInputValue.formatString('dd/MM/yyyy') : '',
            remarksValue: this.remarksInputValue ? this.remarksInputValue : '',
            regardsValue: this.regardsInputValue ? this.regardsInputValue : '',
            hasRemarks: this.remarksInputValue,
            containerArray: this.containersTableBoxSelectedRow.map((row, index) => ({
              num: index + 1,
              pickUpValue: row['Pick Up'],
              requiredDateTime: row['Time Required To'],
              jobNo: row['Job #'],
              shipper: row['Cu Number'],
              pickUpLocation: row['Pick Up'],
              destination: row['Destination'],
              containerNo: row['Cont #'],
              containerSize: row['Size'],
              containerType: row['Type'],
              rqtNo: row['Hau Request No'],
              rqtDate: row['Hau Request Date'],
              recordRemarks: row['Remarks'],
              vessel: row['Vessel Name'],
              etaDate: row['ETA'],
            })),
            _showCompanyLetterHead: this.showCompanyLetterHeadIsChecked,
          });
          break;
      }

      // After everything
      this.designerV3ReportFiles = templateFileNames;
      this.designerV3ReportData = formattedNoticeDetails;

      this.designerV3SharedData = { _header: this.userDetails.CoHeaderFin };
      this.showDesignerV3 = true;
    },
    emailNoticePopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailNoticePopup = false;
        return;
      }

      if (this.emailNoticePopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Emails', 'Please enter at least one valid To email', [{ text: 'OK' }]);
        return;
      }

      if (!this.emailNoticePopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert('warning', 'No Subject', 'Proceed without a subject?', [
          { text: 'Proceed' },
          { text: 'Cancel', color: 'lightgray' },
        ]);
        if (proceedWithoutSubject === 'Cancel') {
          return;
        }
      }
      const requestBody = {
        emaillist: [
          {
            doctype: 'Notice',
            docno: this.designerV3ReportData
              .flatMap((noticeData) => noticeData.containerArray.map((container) => container.containerNo))
              .join(','),
            docnofieldname: '',
            recipient: this.emailNoticePopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailNoticePopupCCMails
              .map((mail) => mail.email)
              .concat([this.userDetails.Email])
              .join(';'), // add the current user's email into CC
            bcc: this.emailNoticePopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailNoticePopupSubjectInputValue.trim(),
            message:
              this.emailNoticePopupMessageText + `\nSender: ${this.userDetails.UserName} (${this.userDetails.Email})`, // add the current user's info in the email body
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/PrintNotice/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailNoticePopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },
    designerV3ShareClicked: function (shareLink) {
      this.emailNoticePopupToMails = [];
      this.emailNoticePopupCCMails = [];
      this.emailNoticePopupBCCMails = [];

      this.emailNoticePopupSubjectInputValue = `Notice: ${this.selectedNoticeType['Notice Type']}`; // put the notice type in the email subject
      this.emailNoticePopupMessageText = `Notice: ${shareLink}\n`;

      this.showEmailNoticePopup = true;
    },
    updateMatchValueList: async function () {
      if (this.selectedFilter) {
        switch (this.selectedFilter.filtertype) {
          case 'Customer Code':
            this.filterMatchValueList = [];
            this.$fullscreenLoading('Getting Customer Code');
            await this.getCustomerCode();
            this.$fullscreenLoading();
            break;
          case 'Destination Code':
            this.filterMatchValueList = [];
            this.$fullscreenLoading('Getting Destination Code');
            await this.getDestinationCode();
            this.$fullscreenLoading();
            break;
          case 'Vessel Name':
            this.filterMatchValueList = [];
            this.$fullscreenLoading('Getting Vessel Name');
            await this.getVesselName();
            this.$fullscreenLoading();
            break;
          case 'Job No':
            this.filterMatchValueList = [];
            this.$fullscreenLoading('Getting Job No');
            await this.getJobNo();
            this.$fullscreenLoading();
            break;
        }
      }
    },

    // Populating filter type list according to selected notice type
    getFilterTypeList: function () {
      if (!this.selectedNoticeType) {
        this.filterTypeList = [
          { filtertype: 'Customer Code', filterno: '1' },
          { filtertype: 'Destination Code', filterno: '2' },
          { filtertype: 'Vessel Name', filterno: '3' },
          { filtertype: 'Job No', filterno: '4' },
          { filtertype: 'Vessel ETA', filterno: '5' },
          { filtertype: 'RFC Request Date', filterno: '6' },
          { filtertype: 'RFC Date', filterno: '7' },
          { filtertype: 'Required Date', filterno: '8' },
        ];
      } else {
        switch (this.selectedNoticeType['Notice Number']) {
          case '1': // Container Ready for Collection
            this.filterTypeList = [
              { filtertype: 'Customer Code', filterno: '1' },
              { filtertype: 'Destination Code', filterno: '2' },
              { filtertype: 'Job No', filterno: '4' },
              { filtertype: 'RFC Request Date', filterno: '6' },
              { filtertype: 'RFC Date', filterno: '7' },
              { filtertype: 'Required Date', filterno: '8' },
            ];
            break;
          case '2': // Daily Outstanding Containers For Delivery
            this.filterTypeList = [
              { filtertype: 'Customer Code', filterno: '1' },
              { filtertype: 'Destination Code', filterno: '2' },
              { filtertype: 'Vessel Name', filterno: '3' },
              { filtertype: 'Job No', filterno: '4' },
              { filtertype: 'Vessel ETA', filterno: '5' },
              { filtertype: 'Required Date', filterno: '8' },
            ];
            break;
          default:
            this.filterTypeList = [
              { filtertype: 'Customer Code', filterno: '1' },
              { filtertype: 'Destination Code', filterno: '2' },
              { filtertype: 'Vessel Name', filterno: '3' },
              { filtertype: 'Job No', filterno: '4' },
              { filtertype: 'Vessel ETA', filterno: '5' },
              { filtertype: 'RFC Request Date', filterno: '6' },
              { filtertype: 'RFC Date', filterno: '7' },
              { filtertype: 'Required Date', filterno: '8' },
            ];
            break;
        }
      }
    },
    // API Call to fetch the list of containers matching filter criteria
    // API Call for Notice Type
    getNoticeType: async function () {
      this.noticeTypeList = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'NOTICETYPE' });
      this.noticeTypeList = result;
    },
    // API Call for Customer
    getCustomerCode: async function () {
      this.filterMatchValueList = [];
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CUSTOMER' }).then((result) => {
        this.filterMatchValueList = result.map((item) => ({
          ...item,
          displayText: `${item['Customer Code']} - ${item['Customer Name']}`,
        }));
      });
    },
    // API Call for Destination Code
    getDestinationCode: async function () {
      this.filterMatchValueList = [];
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'DESTINATION' }).then((result) => {
        this.filterMatchValueList = result.map((item) => ({
          ...item,
          displayText: `${item['Destination Code']} - ${item['Company']}`,
        }));
      });
    },
    // API Call for Job No
    getJobNo: async function () {
      this.filterMatchValueList = [];
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOB' }).then((result) => {
        this.filterMatchValueList = result.map((item) => ({
          ...item,
          displayText: `${item['Job No']}`,
        }));
      });
    },
    // API Call for Job No
    getVesselName: async function () {
      this.filterMatchValueList = [];
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VESSELID' }).then((result) => {
        this.filterMatchValueList = result.map((item) => ({
          ...item,
          displayText: `${item['Vessel Name']}`,
        }));
      });
    },
    checkDateTime: function (field, date) {
      if (date != null && date != '') {
        if (date > this.smallDateTimeMaxDate) {
          const errMsg = `Please select date earlier than ${this.smallDateTimeMaxDate.formatString('yyyy-MM-dd')}`;
          if (field == 'noticeDateInputValue') {
            this.noticeDateErr = errMsg;
          }
          return true;
        } else if (date < this.smallDateTimeMinValue) {
          const errMsg = `Please select date later than ${this.smallDateTimeMinValue.formatString('yyyy-MM-dd')}`;
          if (field == 'noticeDateInputValue') {
            this.noticeDateErr = errMsg;
          }
          return true;
        }
      }
    },
  },
  mounted() {
    const mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.getNoticeType().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'HAULIER' }).then((result) => {
        this.haulierCodeList = result.map((item) => ({
          ...item,
          haulierDisplayText: `${item['Haulier Code']} - ${item['Description']}`,
        }));
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userDetails = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    // For Filter By List when Notice Type Not selected.
    this.getFilterTypeList();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.print-notice-type {
  display: flex;
  gap: 25px;
}
.print-notice-container {
  display: flex;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  font-size: 12px;
  display: flex;
  overflow: hidden;
}
.to-information {
  display: flex;
  gap: 20px;
}
.print-notice-options {
  background-color: white;
  padding: 5px;
  flex: 1;
  overflow-y: auto;
  height: 100%;
}
.print-notice-preview {
  background-color: white;
  margin-left: 5px;
  padding: 5px;
  flex: 1;
  align-content: center;
}
.containers-selection {
  margin-top: 10px;
}
.refresh-button-container {
  display: flex;
  justify-content: flex-end;
  margin: 10px 0;
}

.email-notice-popup {
  .email-notice-popup-content-container {
    display: flex;
    flex-direction: row;

    > .mail-inputs-container {
      margin-right: 5px;
      display: flex;
      flex-direction: column;
      flex: 1;

      > .email-input-container {
        margin-bottom: 5px;

        .note {
          color: gray;
          font-size: 0.85em;
        }
      }
    }

    > .email-content-inputs-container {
      margin-left: 5px;
      display: flex;
      flex-direction: column;
      flex: 2;
    }
  }
}
</style>
